import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Activity, BarChart3, BookOpen, ChevronDown, ClipboardList, Code2, Cpu, Layers,
  ListMusic, LogOut, MapPin, Menu, Monitor, Network, Plug, Radio,
  Server, Settings, UserCheck, Users, X,
} from "lucide-react";
import { RAILSTREAM_LOGO } from "../../lib/api";
import EdgeReleaseHealthBadge from "./EdgeReleaseHealthBadge";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const NAV_GROUPS = [
  {
    id: "operations", label: "Operations", items: [
      { id: "locations", label: "Locations", description: "Sites, buildings, and equipment", icon: MapPin, path: "/locations" },
      { id: "network", label: "Network", description: "WAN, UniFi, and infrastructure", icon: Network, path: "/network" },
      { id: "sites", label: "RailNodes", description: "Source systems and field agents", icon: Monitor, path: "/sites" },
      { id: "multiviews", label: "Live View", description: "Operator multiviews and monitoring", icon: Layers, path: "/multiviews" },
    ],
  },
  {
    id: "delivery", label: "Delivery", items: [
      { id: "transcoders", label: "Distribution", description: "Channels, transcoders, and Edge servers", icon: Cpu, path: "/distribution" },
      { id: "fleet", currentId: "transcoders", label: "Fleet Health", description: "Transcoder service performance", icon: Server, path: "/distribution/fleet" },
      { id: "dvr", currentId: "transcoders", label: "DVR", description: "Storage, recordings, and retention", icon: ClipboardList, path: "/distribution/dvr" },
    ],
  },
  {
    id: "radio", label: "Radio", items: [
      { id: "radio-boxes", label: "RailCast", description: "Server-side radio processing", icon: Radio, path: "/radio/boxes" },
      { id: "radio-feeds", label: "RailFeed", description: "Receiver fleet, audio, and streams", icon: ListMusic, path: "/radio/feeds" },
      { id: "radio-operators", label: "Operators", description: "Radio access and assignments", icon: UserCheck, path: "/radio/operators", adminOnly: true },
    ],
  },
  {
    id: "insights", label: "Insights", items: [
      { id: "analytics", label: "Audience", description: "Viewers, sessions, and activity", icon: BarChart3, path: "/analytics" },
      { id: "performance", label: "Reliability", description: "Alerts, incidents, and performance", icon: Activity, path: "/performance" },
      { id: "devlog", label: "Releases", description: "Versions, changes, and system history", icon: ClipboardList, path: "/devlog" },
    ],
  },
  {
    id: "platform", label: "Platform", items: [
      { id: "documentation", label: "Documentation", description: "Applications, runbooks, and standards", icon: BookOpen, path: "/documentation" },
      { id: "users", label: "Users", description: "People, roles, and access", icon: Users, path: "/users", adminOnly: true },
      { id: "api-reference", label: "API", description: "Endpoints and service URLs", icon: Code2, path: "/api-reference" },
      { id: "integrations", label: "Integrations", description: "Connected systems and credentials", icon: Plug, path: "/integrations", adminOnly: true },
      { id: "settings", label: "Settings", description: "Global Studio configuration", icon: Settings, path: "/settings", adminOnly: true },
    ],
  },
];

const Sidebar = ({ currentPage, onLogout, user, api }) => {
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const isTech = user?.role === "technician";
  const [mobileOpen, setMobileOpen] = useState(false);
  const [studioVersion, setStudioVersion] = useState("");

  useEffect(() => {
    if (!api?.get) return;
    api.get("/versions")
      .then((response) => setStudioVersion(response?.data?.studio_version || ""))
      .catch(() => setStudioVersion(""));
  }, [api]);

  const roleBadge = user?.role === "super_admin" ? "Super Admin"
    : user?.role === "admin" ? "Admin"
      : isTech ? "Technician"
        : user?.role === "viewer" ? "Viewer" : "Operator";
  const versionLabel = studioVersion ? `v${studioVersion}` : "";
  const visibleGroups = NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((item) => !item.adminOnly || isAdmin),
  })).filter((group) => group.items.length);

  const go = (path) => {
    navigate(path);
    setMobileOpen(false);
  };
  const itemIsActive = (item) => item.id === currentPage || item.currentId === currentPage;
  const groupIsActive = (group) => group.items.some(itemIsActive);

  const NavItem = ({ item, mobile = false }) => {
    const Icon = item.icon;
    const active = itemIsActive(item);
    if (mobile) {
      return (
        <button type="button" data-testid={`nav-mobile-${item.id}`} onClick={() => go(item.path)}
          className={`flex w-full items-start gap-3 rounded-lg px-3 py-2.5 text-left transition-colors ${active ? "bg-amber-500/15 text-amber-300" : "text-zinc-200 hover:bg-zinc-800"}`}>
          <Icon className="mt-0.5 h-4 w-4 shrink-0" />
          <span className="min-w-0">
            <span className="block text-sm font-medium">{item.label}</span>
            <span className="mt-0.5 block text-xs leading-4 text-zinc-500">{item.description}</span>
          </span>
        </button>
      );
    }
    return (
      <DropdownMenuItem data-testid={`nav-${item.id}`} onSelect={() => go(item.path)}
        className={`my-0.5 flex cursor-pointer items-start gap-3 rounded-md px-3 py-2.5 focus:bg-zinc-800 focus:text-white ${active ? "bg-amber-500/10 text-amber-300" : "text-zinc-200"}`}>
        <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${active ? "text-amber-400" : "text-zinc-400"}`} />
        <span className="min-w-0">
          <span className="block text-sm font-medium leading-5">{item.label}</span>
          <span className="block text-xs leading-4 text-zinc-500">{item.description}</span>
        </span>
      </DropdownMenuItem>
    );
  };

  return (
    <>
      <header className="hidden h-16 shrink-0 items-center border-b border-zinc-800 bg-zinc-900/95 px-4 lg:flex" data-testid="studio-top-navigation">
        <button type="button" onClick={() => go("/")} className="mr-5 flex shrink-0 items-center gap-2.5 pr-5" aria-label="Open Command Center">
          <img src={RAILSTREAM_LOGO} alt="RailStream" className="h-9 w-9 object-contain" />
          <span className="text-left leading-tight">
            <span className="block text-sm font-semibold text-white">RailStream™</span>
            <span className="block text-xs text-zinc-400">Studio</span>
          </span>
        </button>
        <div className="h-8 w-px shrink-0 bg-zinc-700" aria-hidden="true" />

        <nav className="ml-4 flex min-w-0 items-center gap-1" aria-label="Primary navigation">
          <button type="button" data-testid="nav-dashboard" onClick={() => go("/")}
            className={`h-10 rounded-md px-3 text-sm font-medium transition-colors ${currentPage === "dashboard" ? "bg-amber-500/15 text-amber-300" : "text-zinc-300 hover:bg-zinc-800 hover:text-white"}`}>
            Command Center
          </button>
          {visibleGroups.map((group) => (
            <DropdownMenu key={group.id}>
              <DropdownMenuTrigger asChild>
                <button type="button" data-testid={`nav-group-${group.id}`}
                  className={`flex h-10 items-center gap-1 rounded-md px-3 text-sm font-medium outline-none transition-colors ${groupIsActive(group) ? "bg-amber-500/15 text-amber-300" : "text-zinc-300 hover:bg-zinc-800 hover:text-white"}`}>
                  {group.label}<ChevronDown className="h-3.5 w-3.5 opacity-70" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" sideOffset={9} className="w-80 border-zinc-700 bg-zinc-900 p-2 shadow-2xl">
                <DropdownMenuLabel className="px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-500">{group.label}</DropdownMenuLabel>
                <DropdownMenuSeparator className="mx-1 mb-1 bg-zinc-700" />
                {group.items.map((item) => <NavItem key={item.id} item={item} />)}
                {group.id === "platform" && isAdmin && (
                  <><DropdownMenuSeparator className="mx-1 mt-2 bg-zinc-700" /><div className="px-3 py-2"><EdgeReleaseHealthBadge api={api} user={user} /></div></>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          ))}
        </nav>

        <div className="ml-auto flex shrink-0 items-center gap-3 pl-4">
          {versionLabel && <span className="text-xs text-zinc-500" data-testid="studio-version">{versionLabel}</span>}
          <div className="h-8 w-px bg-zinc-700" aria-hidden="true" />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button" className="flex h-10 items-center gap-2 rounded-md px-2 text-left hover:bg-zinc-800">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-amber-500/15 text-xs font-semibold text-amber-400">{user?.username?.charAt(0).toUpperCase()}</span>
                <span className="hidden xl:block">
                  <span className="block max-w-28 truncate text-xs font-medium text-white">{user?.username}</span>
                  <span className="block text-[10px] text-zinc-500">{roleBadge}</span>
                </span>
                <ChevronDown className="h-3.5 w-3.5 text-zinc-500" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" sideOffset={9} className="w-56 border-zinc-700 bg-zinc-900 p-2">
              <DropdownMenuLabel className="px-3 py-2"><span className="block text-sm text-white">{user?.username}</span><span className="block text-xs font-normal text-zinc-500">{roleBadge}</span></DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-zinc-700" />
              <DropdownMenuItem onSelect={onLogout} className="cursor-pointer px-3 py-2 text-red-400 focus:bg-red-500/10 focus:text-red-300"><LogOut className="h-4 w-4" /> Logout</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <header className="flex h-14 shrink-0 items-center justify-between border-b border-zinc-800 bg-zinc-900 px-3 lg:hidden">
        <button type="button" onClick={() => setMobileOpen(true)} className="rounded-md p-2 text-zinc-300 hover:bg-zinc-800" aria-label="Open navigation" data-testid="sidebar-hamburger"><Menu className="h-5 w-5" /></button>
        <button type="button" onClick={() => go("/")} className="flex items-center gap-2"><img src={RAILSTREAM_LOGO} alt="RailStream" className="h-7 w-7 object-contain" /><span className="text-sm font-semibold text-white">RailStream Studio</span></button>
        <span className="w-9 text-right text-[10px] text-zinc-500">{versionLabel}</span>
      </header>

      {mobileOpen && (
        <>
          <button type="button" className="fixed inset-0 z-40 bg-black/70" onClick={() => setMobileOpen(false)} aria-label="Close navigation backdrop" />
          <aside className="fixed inset-y-0 left-0 z-50 flex w-[min(22rem,90vw)] flex-col overflow-hidden border-r border-zinc-700 bg-zinc-900 shadow-2xl" data-testid="mobile-navigation">
            <div className="flex h-16 shrink-0 items-center justify-between border-b border-zinc-700 px-4">
              <div className="flex items-center gap-2.5"><img src={RAILSTREAM_LOGO} alt="RailStream" className="h-8 w-8 object-contain" /><div><p className="text-sm font-semibold text-white">RailStream Studio</p><p className="text-xs text-zinc-500">{roleBadge}</p></div></div>
              <button type="button" onClick={() => setMobileOpen(false)} className="rounded-md p-2 text-zinc-400 hover:bg-zinc-800 hover:text-white" aria-label="Close navigation"><X className="h-5 w-5" /></button>
            </div>
            <nav className="min-h-0 flex-1 overflow-y-auto px-3 py-3" data-testid="sidebar-scroll-owner">
              <button type="button" onClick={() => go("/")} className={`mb-3 flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left text-sm font-semibold ${currentPage === "dashboard" ? "bg-amber-500/15 text-amber-300" : "text-white hover:bg-zinc-800"}`}><Activity className="h-4 w-4" /> Command Center</button>
              {visibleGroups.map((group) => (
                <section key={group.id} className="border-t border-zinc-700 py-3 first:border-t-0">
                  <h2 className="px-3 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500">{group.label}</h2>
                  <div className="space-y-1">{group.items.map((item) => <NavItem key={item.id} item={item} mobile />)}</div>
                </section>
              ))}
            </nav>
            <div className="shrink-0 border-t border-zinc-700 p-3">
              <div className="mb-2 px-3"><p className="text-sm text-white">{user?.username}</p><p className="text-xs text-zinc-500">{versionLabel}</p></div>
              <button type="button" onClick={onLogout} className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-red-400 hover:bg-red-500/10"><LogOut className="h-4 w-4" /> Logout</button>
            </div>
          </aside>
        </>
      )}
    </>
  );
};

export default Sidebar;
